import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Button, Paper, TextField, Typography, CircularProgress, Grid,
    FormControl, InputLabel, Select, MenuItem, Chip, Tooltip, Switch,
    LinearProgress, Alert, Collapse, IconButton, Dialog, DialogTitle,
    DialogContent, DialogContentText, DialogActions
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { useFirebase } from '../../context/FirebaseContext';
import {
    getCalendarStatusV2,
    refreshCalendarV2,
    getCalendarEventsV2,
    simulateEventProximityV2,
    setCalendarProviderEnabledV2,
    triggerIntegrityCheckV2,
    clearAllCalendarEventsV2
} from '../../api/admin';
import './AdminView.css';

const COUNTRIES = [
    { value: '', label: 'All Countries' },
    { value: 'United States', label: 'United States' },
    { value: 'Euro Area', label: 'Euro Area' },
    { value: 'United Kingdom', label: 'United Kingdom' },
    { value: 'Japan', label: 'Japan' },
    { value: 'Canada', label: 'Canada' },
    { value: 'Australia', label: 'Australia' },
    { value: 'New Zealand', label: 'New Zealand' },
    { value: 'Switzerland', label: 'Switzerland' },
    { value: 'China', label: 'China' },
    { value: 'Germany', label: 'Germany' },
];

const IMPACT_LEVELS = [
    { value: '', label: 'All Impacts' },
    { value: 'HIGH', label: 'High' },
    { value: 'MEDIUM', label: 'Medium' },
    { value: 'LOW', label: 'Low' },
];

const INSTRUMENTS = [
    { value: '', label: 'No Instrument Filter' },
    { value: 'EURUSD', label: 'EUR/USD' },
    { value: 'GBPUSD', label: 'GBP/USD' },
    { value: 'USDJPY', label: 'USD/JPY' },
    { value: 'USDCAD', label: 'USD/CAD' },
    { value: 'AUDUSD', label: 'AUD/USD' },
    { value: 'NZDUSD', label: 'NZD/USD' },
    { value: 'USDCHF', label: 'USD/CHF' },
    { value: 'EURJPY', label: 'EUR/JPY' },
    { value: 'GBPJPY', label: 'GBP/JPY' },
    { value: 'EURGBP', label: 'EUR/GBP' },
];

const formatDateTime = (dateTimeStr) => {
    if (!dateTimeStr) return '-';
    // Ensure UTC interpretation by appending Z if missing
    // Handle case where dateTimeStr might not be a string (though it should be)
    const strVal = String(dateTimeStr);
    const dateStr = strVal.endsWith('Z') ? strVal : `${strVal}Z`;
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
    });
};

const formatRelativeTime = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
};

const getImpactColor = (impact) => {
    switch (impact?.toUpperCase()) {
        case 'HIGH': return 'error';
        case 'MEDIUM': return 'warning';
        case 'LOW': return 'success';
        default: return 'default';
    }
};

const getTimeStatus = (dateTimeStr) => {
    if (!dateTimeStr) return { label: '-', color: 'default' };
    const strVal = String(dateTimeStr);
    const eventTime = new Date(strVal.endsWith('Z') ? strVal : `${strVal}Z`);
    const now = new Date();
    const diffMinutes = Math.round((eventTime - now) / (1000 * 60));

    if (diffMinutes < -60) {
        return { label: `${Math.abs(Math.round(diffMinutes / 60))}h ago`, color: 'default' };
    } else if (diffMinutes < 0) {
        return { label: `${Math.abs(diffMinutes)}m ago`, color: 'default' };
    } else if (diffMinutes < 30) {
        return { label: `In ${diffMinutes}m`, color: 'error' };
    } else if (diffMinutes < 60) {
        return { label: `In ${diffMinutes}m`, color: 'warning' };
    } else if (diffMinutes < 24 * 60) {
        return { label: `In ${Math.round(diffMinutes / 60)}h`, color: 'success' };
    } else {
        return { label: `In ${Math.round(diffMinutes / (60 * 24))}d`, color: 'default' };
    }
};

const EconomicCalendarTab = () => {
    const { auth } = useFirebase();
    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [status, setStatus] = useState(null);
    const [events, setEvents] = useState([]);
    const [eventsCount, setEventsCount] = useState(0);

    // Filters
    const [country, setCountry] = useState('');
    const [impact, setImpact] = useState('');
    const [instrument, setInstrument] = useState('');
    const [hoursBack, setHoursBack] = useState(48);
    const [hoursForward, setHoursForward] = useState(168);

    // Simulation
    const [simulating, setSimulating] = useState(false);
    const [simInstrument, setSimInstrument] = useState('EURUSD');
    const [simBlockBefore, setSimBlockBefore] = useState(30);
    const [simBlockAfter, setSimBlockAfter] = useState(15);
    const [simMinImpactLevel, setSimMinImpactLevel] = useState('HIGH');
    const [simResult, setSimResult] = useState(null);

    // Integrity Check
    const [checkingIntegrity, setCheckingIntegrity] = useState(false);

    // Clear Events
    const [clearing, setClearing] = useState(false);
    const [clearConfirmOpen, setClearConfirmOpen] = useState(false);
    const [clearResult, setClearResult] = useState(null);

    // Coverage info
    const [showCoverageInfo, setShowCoverageInfo] = useState(false);

    const loadStatus = useCallback(async () => {
        try {
            const response = await new Promise((resolve) => {
                getCalendarStatusV2(auth, resolve);
            });
            setStatus(response);
        } catch (error) {
            console.error('Error loading calendar status:', error);
        }
    }, [auth]);

    // Load status on mount
    useEffect(() => {
        loadStatus();
    }, [loadStatus]);

    const handleRefreshCalendar = async () => {
        setRefreshing(true);
        try {
            const response = await new Promise((resolve) => {
                refreshCalendarV2(auth, resolve);
            });
            setStatus(response);
            // Reload events after refresh
            await loadEvents();
        } catch (error) {
            console.error('Error refreshing calendar:', error);
        } finally {
            setRefreshing(false);
        }
    };

    const loadEvents = async () => {
        setLoading(true);
        try {
            const filters = {
                country,
                impact,
                instrument,
                hoursBack,
                hoursForward
            };
            const response = await new Promise((resolve) => {
                getCalendarEventsV2(auth, resolve, filters);
            });
            setEvents(response.events || []);
            setEventsCount(response.count || 0);
        } catch (error) {
            console.error('Error loading events:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleProviderToggle = async (providerName, currentEnabled) => {
        try {
            await new Promise((resolve) => {
                setCalendarProviderEnabledV2(auth, providerName, !currentEnabled, resolve);
            });
            // Reload status to get updated provider states
            await loadStatus();
        } catch (error) {
            console.error('Error toggling provider:', error);
        }
    };

    const handleIntegrityCheck = async () => {
        setCheckingIntegrity(true);
        try {
            // Use the API client function instead of raw fetch
            await new Promise((resolve) => {
                triggerIntegrityCheckV2(auth, resolve);
            });

            // Reload status to get the updated report
            await loadStatus();
        } catch (error) {
            console.error('Error running integrity check:', error);
        } finally {
            setCheckingIntegrity(false);
        }
    };

    const handleClearAllEvents = async () => {
        setClearConfirmOpen(false);
        setClearing(true);
        setClearResult(null);
        try {
            const response = await new Promise((resolve) => {
                clearAllCalendarEventsV2(auth, resolve);
            });
            setClearResult(response);
            // Reload status to show updated counts
            await loadStatus();
        } catch (error) {
            console.error('Error clearing calendar events:', error);
            setClearResult({ error: error.message });
        } finally {
            setClearing(false);
        }
    };

    const handleSimulate = async () => {
        setSimulating(true);
        setSimResult(null);
        try {
            const config = {
                instrument: simInstrument,
                blockBeforeMin: parseInt(simBlockBefore, 10),
                blockAfterMin: parseInt(simBlockAfter, 10),
                minImpactLevel: simMinImpactLevel
            };
            const response = await new Promise((resolve) => {
                simulateEventProximityV2(auth, resolve, config);
            });
            setSimResult(response);
        } catch (error) {
            console.error('Error simulating:', error);
            setSimResult({ error: error.message });
        } finally {
            setSimulating(false);
        }
    };

    return (
        <Box className="admin-tab-content">
            {/* Service Status Section */}
            <Paper className="admin-section">
                <Box className="admin-section-header">
                    <Typography variant="h6">Calendar Service Status</Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            variant="outlined"
                            onClick={() => setClearConfirmOpen(true)}
                            disabled={clearing}
                            size="small"
                            color="error"
                            startIcon={clearing ? <CircularProgress size={16} /> : <DeleteForeverIcon />}
                        >
                            Clear All Events
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={() => handleIntegrityCheck()}
                            disabled={checkingIntegrity}
                            size="small"
                            color="info"
                            startIcon={checkingIntegrity ? <CircularProgress size={16} /> : <CheckCircleIcon />}
                        >
                            Integrity Check
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={handleRefreshCalendar}
                            disabled={refreshing}
                            size="small"
                            startIcon={refreshing ? <CircularProgress size={16} /> : <RefreshIcon />}
                        >
                            Refresh Now
                        </Button>
                    </Box>
                </Box>

                {/* Clear Events Confirmation Dialog */}
                <Dialog open={clearConfirmOpen} onClose={() => setClearConfirmOpen(false)}>
                    <DialogTitle>Clear All Calendar Events?</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            This will delete ALL calendar events from the database.
                            You can reload events by clicking "Refresh Now" afterwards.
                            This action cannot be undone.
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setClearConfirmOpen(false)}>Cancel</Button>
                        <Button onClick={handleClearAllEvents} color="error" variant="contained">
                            Clear All Events
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Clear Result Alert */}
                {clearResult && (
                    <Alert
                        severity={clearResult.error ? 'error' : 'success'}
                        onClose={() => setClearResult(null)}
                        sx={{ mb: 2 }}
                    >
                        {clearResult.error
                            ? `Error: ${clearResult.error}`
                            : `Successfully cleared ${clearResult.deletedCount} calendar events`
                        }
                    </Alert>
                )}

                {status ? (
                    <>
                        <Grid container spacing={2} className="admin-status-grid">
                            <Grid item xs={6} sm={3}>
                                <Typography variant="caption" color="text.secondary">Status</Typography>
                                <Typography className={status.lastRefreshStatus?.includes('Success') ? 'admin-text-success' : 'admin-text-warning'}>
                                    {status.lastRefreshStatus || 'Unknown'}
                                </Typography>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <Typography variant="caption" color="text.secondary">Events in DB</Typography>
                                <Typography>{status.totalEventsInDb || 0}</Typography>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <Typography variant="caption" color="text.secondary">Days Retained</Typography>
                                <Typography>{status.daysToKeep || '-'} days</Typography>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <Typography variant="caption" color="text.secondary">Upcoming High-Impact (48h)</Typography>
                                <Typography>{status.upcomingHighImpactCount || 0} events</Typography>
                            </Grid>
                        </Grid>

                        {/* Integrity Report */}
                        {status.integrityReport && (
                            <Box sx={{ mt: 2, p: 1.5, bgcolor: 'background.default', borderRadius: 1 }}>
                                <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                    Integrity Status:
                                    <Chip
                                        label={status.integrityReport.status}
                                        color={status.integrityReport.status === 'OK' ? 'success' : status.integrityReport.status === 'PARTIAL' ? 'info' : 'warning'}
                                        size="small"
                                    />
                                    {status.integrityReport.coveragePercent !== undefined && (
                                        <Chip
                                            label={`${status.integrityReport.coveragePercent}% coverage`}
                                            size="small"
                                            variant="outlined"
                                        />
                                    )}
                                    {status.integrityReport.checkTime && (
                                        <Typography variant="caption" color="text.secondary">
                                            (Checked: {new Date(status.integrityReport.checkTime).toLocaleString()})
                                        </Typography>
                                    )}
                                </Typography>
                                <Typography variant="body2" sx={{ mt: 0.5 }}>
                                    Reference Events: {status.integrityReport.referenceEventCount || 0} | DB Events: {status.integrityReport.dbEventCount || 0} | Missing: {status.integrityReport.missingCount || 0}
                                </Typography>
                                {status.integrityReport.missingEvents?.length > 0 && (
                                    <Box sx={{ mt: 1 }}>
                                        <Typography variant="caption" sx={{ fontWeight: 600 }}>Missing Events:</Typography>
                                        {status.integrityReport.missingEvents.slice(0, 5).map((evt, i) => (
                                            <Typography key={i} variant="caption" display="block" sx={{ ml: 1 }}>
                                                • <strong>{evt.title}</strong> ({evt.country}) - {evt.date ? new Date(evt.date).toLocaleDateString() : 'Unknown date'} - <Chip label={evt.impact} size="small" color={evt.impact === 'High' ? 'error' : 'warning'} sx={{ height: 16, fontSize: '0.65rem' }} />
                                            </Typography>
                                        ))}
                                    </Box>
                                )}
                            </Box>
                        )}

                        {/* Events by Provider */}
                        {status.eventsByProvider && Object.keys(status.eventsByProvider).length > 0 && (
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                    Events by Provider
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                    {Object.entries(status.eventsByProvider).map(([provider, count]) => (
                                        <Chip
                                            key={provider}
                                            label={`${provider}: ${count}`}
                                            size="small"
                                            variant="outlined"
                                        />
                                    ))}
                                </Box>
                            </Box>
                        )}
                    </>
                ) : (
                    <Box className="admin-empty-prompt">
                        <Typography color="text.secondary">Loading status...</Typography>
                    </Box>
                )}
            </Paper>

            {/* Coverage Info Alert */}
            <Alert
                severity="info"
                icon={<InfoIcon />}
                action={
                    <IconButton
                        size="small"
                        onClick={() => setShowCoverageInfo(!showCoverageInfo)}
                    >
                        {showCoverageInfo ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                }
                sx={{ mb: 2 }}
            >
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Country Coverage: Major forex pairs only (USD, EUR, GBP, JPY, CAD, AUD, NZD, CHF)
                </Typography>
                <Collapse in={showCoverageInfo}>
                    <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" component="div" color="text.secondary">
                            <strong>Myfxbook RSS:</strong> US, UK, Euro Area, Japan, Canada, Australia, NZ, Switzerland, China, Germany, France, Italy, Spain, South Korea, South Africa, Israel, Mexico, Brazil, India, Russia
                        </Typography>
                        <Typography variant="caption" component="div" color="text.secondary" sx={{ mt: 0.5 }}>
                            <strong>TradingEconomics:</strong> US, Euro Area, Canada, NZ, Switzerland, Japan, Australia, China, Hong Kong, G7, UK
                        </Typography>
                        <Typography variant="caption" component="div" color="text.secondary" sx={{ mt: 0.5 }}>
                            <strong>Not covered:</strong> Nordic (NOK, SEK, DKK), Singapore (SGD), Turkey (TRY), Poland (PLN), Hungary (HUF), Czech (CZK), Thailand (THB)
                        </Typography>
                    </Box>
                </Collapse>
            </Alert>

            {/* Provider Health Section */}
            {status?.providers && Array.isArray(status.providers) && status.providers.length > 0 && (
                <Paper className="admin-section">
                    <Box className="admin-section-header">
                        <Typography variant="h6">Provider Health</Typography>
                    </Box>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Provider</TableCell>
                                    <TableCell>Enabled</TableCell>
                                    <TableCell>Last Run</TableCell>
                                    <TableCell>Success Rate</TableCell>
                                    <TableCell>Events Fetched</TableCell>
                                    <TableCell>Avg Response</TableCell>
                                    <TableCell>Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {status.providers.map((provider) => {
                                    const successRate = provider.success_rate || 0;
                                    const isHealthy = provider.last_error_time == null ||
                                        (provider.last_success_time && new Date(provider.last_success_time) > new Date(provider.last_error_time));

                                    return (
                                        <TableRow key={provider.provider} hover>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                    {provider.provider}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Switch
                                                    size="small"
                                                    checked={provider.is_enabled}
                                                    onChange={() => handleProviderToggle(provider.provider, provider.is_enabled)}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Tooltip title={provider.last_run_time || 'Never'}>
                                                    <span>{formatRelativeTime(provider.last_run_time)}</span>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <LinearProgress
                                                        variant="determinate"
                                                        value={successRate}
                                                        sx={{ width: 60, height: 6, borderRadius: 3 }}
                                                        color={successRate >= 80 ? 'success' : successRate >= 50 ? 'warning' : 'error'}
                                                    />
                                                    <Typography variant="caption">{successRate}%</Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>{provider.events_fetched || 0}</TableCell>
                                            <TableCell>{provider.avg_response_time_ms ? `${provider.avg_response_time_ms}ms` : '-'}</TableCell>
                                            <TableCell>
                                                {isHealthy ? (
                                                    <Chip
                                                        icon={<CheckCircleIcon />}
                                                        label="Healthy"
                                                        size="small"
                                                        color="success"
                                                        variant="outlined"
                                                    />
                                                ) : (
                                                    <Tooltip title={provider.last_error_message || 'Unknown error'}>
                                                        <Chip
                                                            icon={<ErrorIcon />}
                                                            label="Error"
                                                            size="small"
                                                            color="error"
                                                            variant="outlined"
                                                        />
                                                    </Tooltip>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}

            {/* Events Table Section */}
            <Paper className="admin-section">
                <Box className="admin-section-header">
                    <Typography variant="h6">Economic Events</Typography>
                    <Button
                        variant="contained"
                        onClick={loadEvents}
                        disabled={loading}
                        size="small"
                        startIcon={loading ? <CircularProgress size={16} /> : <PlayArrowIcon />}
                    >
                        Load Events
                    </Button>
                </Box>

                {/* Filters */}
                <Grid container spacing={2} className="admin-filter-grid" sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={6} md={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Country</InputLabel>
                            <Select value={country} label="Country" onChange={(e) => setCountry(e.target.value)}>
                                {COUNTRIES.map(c => (
                                    <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Impact</InputLabel>
                            <Select value={impact} label="Impact" onChange={(e) => setImpact(e.target.value)}>
                                {IMPACT_LEVELS.map(i => (
                                    <MenuItem key={i.value} value={i.value}>{i.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Instrument</InputLabel>
                            <Select value={instrument} label="Instrument" onChange={(e) => setInstrument(e.target.value)}>
                                {INSTRUMENTS.map(i => (
                                    <MenuItem key={i.value} value={i.value}>{i.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={6} sm={3} md={2}>
                        <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label="Hours Back"
                            value={hoursBack}
                            onChange={(e) => setHoursBack(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={6} sm={3} md={2}>
                        <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label="Hours Forward"
                            value={hoursForward}
                            onChange={(e) => setHoursForward(e.target.value)}
                        />
                    </Grid>
                </Grid>

                {/* Results Count */}
                {eventsCount > 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Showing {events.length} events
                    </Typography>
                )}

                {/* Events Table */}
                {events.length > 0 ? (
                    <TableContainer sx={{ maxHeight: 400 }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Time (Local)</TableCell>
                                    <TableCell>Event</TableCell>
                                    <TableCell>Country</TableCell>
                                    <TableCell>Currency</TableCell>
                                    <TableCell>Impact</TableCell>
                                    <TableCell>Category</TableCell>
                                    <TableCell>Actual</TableCell>
                                    <TableCell>Forecast</TableCell>
                                    <TableCell>Previous</TableCell>
                                    <TableCell>Provider</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {events.map((event, idx) => {
                                    const timeStatus = getTimeStatus(event.dateTime);
                                    return (
                                        <TableRow key={idx} hover>
                                            <TableCell>
                                                <Tooltip title={formatDateTime(event.dateTime)}>
                                                    <Chip
                                                        label={timeStatus.label}
                                                        size="small"
                                                        color={timeStatus.color}
                                                        variant="outlined"
                                                    />
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontWeight: 500, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {event.title}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>{event.country}</TableCell>
                                            <TableCell>
                                                <Chip label={event.currency || '-'} size="small" variant="outlined" />
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={event.impact || '-'}
                                                    size="small"
                                                    color={getImpactColor(event.impact)}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="caption" color="text.secondary">
                                                    {event.category || '-'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>{event.actual || '-'}</TableCell>
                                            <TableCell>{event.forecast || '-'}</TableCell>
                                            <TableCell>{event.previous || '-'}</TableCell>
                                            <TableCell>
                                                <Typography variant="caption" color="text.secondary">
                                                    {event.provider || '-'}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                ) : loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Box className="admin-empty-prompt">
                        <Typography color="text.secondary">
                            Click "Load Events" to fetch calendar events
                        </Typography>
                    </Box>
                )}
            </Paper>

            {/* Simulation Section */}
            <Paper className="admin-section">
                <Box className="admin-section-header">
                    <Typography variant="h6">EventProximityGate Simulator</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Test how EventProximityGate would evaluate a trade right now.
                </Typography>

                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Instrument</InputLabel>
                            <Select value={simInstrument} label="Instrument" onChange={(e) => setSimInstrument(e.target.value)}>
                                {INSTRUMENTS.filter(i => i.value).map(i => (
                                    <MenuItem key={i.value} value={i.value}>{i.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={6} sm={2}>
                        <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label="Block Before (min)"
                            value={simBlockBefore}
                            onChange={(e) => setSimBlockBefore(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={6} sm={2}>
                        <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label="Block After (min)"
                            value={simBlockAfter}
                            onChange={(e) => setSimBlockAfter(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Min Impact Level</InputLabel>
                            <Select value={simMinImpactLevel} label="Min Impact Level" onChange={(e) => setSimMinImpactLevel(e.target.value)}>
                                <MenuItem value="HIGH">HIGH only</MenuItem>
                                <MenuItem value="MEDIUM">MEDIUM and above</MenuItem>
                                <MenuItem value="LOW">All (incl. LOW)</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>

                <Button
                    variant="contained"
                    onClick={handleSimulate}
                    disabled={simulating}
                    startIcon={simulating ? <CircularProgress size={16} /> : <PlayArrowIcon />}
                >
                    Run Simulation
                </Button>

                {simResult && (
                    <Box sx={{ mt: 2 }}>
                        <Paper
                            className={`admin-sim-result ${simResult.wouldBlock ? 'admin-sim-fail' : 'admin-sim-pass'}`}
                            sx={{ p: 2 }}
                        >
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                {simResult.verdict}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                Instrument: {simResult.instrument} |
                                Checked at: {simResult.checkTime} |
                                Countries: {simResult.countriesChecked?.join(', ')}
                            </Typography>
                            {simResult.blockingEvents?.length > 0 && (
                                <Box sx={{ mt: 2 }}>
                                    <Typography variant="subtitle2">Blocking Events:</Typography>
                                    {simResult.blockingEvents.map((evt, idx) => (
                                        <Box key={idx} sx={{ ml: 2, mt: 0.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                            <Chip
                                                label={evt.impact}
                                                size="small"
                                                color={evt.impact === 'HIGH' ? 'error' : evt.impact === 'MEDIUM' ? 'warning' : 'default'}
                                            />
                                            <Typography variant="body2">
                                                <strong>{evt.title}</strong> ({evt.country})
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {evt.dateTime ? new Date(evt.dateTime + 'Z').toLocaleString() : ''}
                                                ({evt.position === 'upcoming' ? `in ${evt.minutesToEvent}m` : `${Math.abs(evt.minutesToEvent)}m ago`})
                                            </Typography>
                                            {evt.provider && <Typography component="span" variant="caption" color="text.secondary">[{evt.provider}]</Typography>}
                                        </Box>
                                    ))}
                                </Box>
                            )}
                        </Paper>
                    </Box>
                )}
            </Paper>
        </Box>
    );
};

export default EconomicCalendarTab;
